import { useState } from 'react';
import { BookCopy, Ellipsis, FileDown, FileUp } from 'lucide-react';

import DownloadCarouselCSVDialog from 'components/production/components/AisleContent/DownloadCarouselCSVDialog';
import UploadProductCSVDialog from 'components/production/components/AisleContent/UploadProductCSVDialog';
import { downloadCarouselCSV } from 'components/production/components/AisleContent/utils';
import DateWeekInput from 'components/storefront/components/DateWeekInput';
import PackGroupInput from 'components/storefront/components/PackGroupInput';
import { Button } from 'shadcn/components/ui/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'shadcn/components/ui/DropdownMenu';

import DuplicateCarouselsDialog from './DuplicateCarouselsDialog';

/**
 * Displays a header with date and pack group selection, as well as additional tooling options.
 *
 * @param {Array} aisles - The list of aisles
 * @param {Array} carousels - The list of carousels
 * @param {Date} date - The currently selected date
 * @param {boolean} datePopoverOpen - Whether the date popover is open
 * @param {Function} handleSelectDate - Callback function called when a new date is selected
 * @param {Function} handleSelectPackGroup - Callback function called when a new pack group is selected
 * @param {Array} packGroups - The list of pack groups
 * @param {Function} refetchCarousels - Function to refetch the carousels
 * @param {number} selectedPackGroupID - The currently selected pack group ID
 * @param {Function} setDatePopoverOpen - Callback function called when the date popover is opened or closed
 */
export default function Header({
    aisles,
    carousels,
    date,
    datePopoverOpen,
    handleSelectDate,
    handleSelectPackGroup,
    packGroups,
    refetchCarousels,
    selectedPackGroupID,
    setDatePopoverOpen,
}) {
    const [duplicateCarouselsDialogOpen, setDuplicateCarouselsDialogOpen] = useState(false);
    const [isDownloadCarouselCSVDialogOpen, setIsDownloadCarouselCSVDialogOpen] = useState(false);
    const [isUploadProductCSVDialogOpen, setIsUploadProductCSVDialogOpen] = useState(false);

    const handleDownloadCarouselCSV = () => {
        downloadCarouselCSV(aisles, carousels, selectedPackGroupID);
        setIsDownloadCarouselCSVDialogOpen(false);
    };

    return (
        <>
            <div className="w-full h-13" />
            <div className="fixed top-[64px] flex justify-center gap-2 w-screen border-b h-18 px-4 -ml-5 bg-[#F6F6F6] z-50">
                <div className="flex-1 flex items-center justify-between gap-4 w-full max-w-7xl">
                    <h1 className="text-2xl font-bold">Aisle Content</h1>
                    <div className="flex gap-2">
                        <DateWeekInput
                            date={date}
                            onSelectDate={handleSelectDate}
                            popoverOpen={datePopoverOpen}
                            setPopoverOpen={setDatePopoverOpen}
                        />
                        <PackGroupInput
                            packGroups={packGroups}
                            selectedPackGroupID={selectedPackGroupID}
                            setSelectedPackGroupID={handleSelectPackGroup}
                        />
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="relative hover:bg-border data-[state=open]:bg-border data-[state=open]:hover:bg-border"
                                >
                                    <Ellipsis />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setDuplicateCarouselsDialogOpen(true)}>
                                    <BookCopy />
                                    Duplicate carousels
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setIsUploadProductCSVDialogOpen(true)}>
                                    <FileUp />
                                    Upload product CSV
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setIsDownloadCarouselCSVDialogOpen(true)}>
                                    <FileDown />
                                    Download carousel CSV
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </div>

            <DuplicateCarouselsDialog
                carousels={carousels}
                initialDate={date}
                isOpen={duplicateCarouselsDialogOpen}
                onClose={() => setDuplicateCarouselsDialogOpen(false)}
            />
            <UploadProductCSVDialog
                isOpen={isUploadProductCSVDialogOpen}
                onClose={() => setIsUploadProductCSVDialogOpen(false)}
                refetchCarousels={refetchCarousels}
            />
            <DownloadCarouselCSVDialog
                isOpen={isDownloadCarouselCSVDialogOpen}
                onClose={() => setIsDownloadCarouselCSVDialogOpen(false)}
                onConfirm={handleDownloadCarouselCSV}
            />
        </>
    );
}
